// resources/js/Pages/Cities/Edit.tsx
import React, { useState, useEffect } from 'react';
import { router } from '@inertiajs/react';
import {
  Container,
  Paper,
  Title,
  TextInput,
  Select,
  Button,
  Group,
  Stack,
  Alert,
  Box,
  Breadcrumbs,
  Anchor,
  SimpleGrid,
  Text,
  MantineProvider,
  createTheme,
  Loader,
  Flex,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconAlertCircle, IconDeviceFloppy, IconArrowLeft, IconCheck } from '@tabler/icons-react';

import { StablePagination } from '@/components/StablePagination';

import { EventCard } from '../../../components/ui';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

interface City {
  id: number;
  name: string;
  area_id: number | null;
  country_id: number | null;
}

interface Country {
  id: number;
  name: string;
  continent_name: string;
}

interface Area {
  id: number;
  name: string;
  country_id: number;
}

interface Event {
  id: number;
  name: string;
  event_start_date: string;
  event_end_date: string;
  description: string;
  event_types: { id: number; name: string }[];
  dance_styles?: { id: number; name: string }[];
  venue_name: string;
  address: string;
  city?: { name: string };
  state?: { name: string };
  country?: {
    name: string;
    flag_emoji: string;
  };
  ticket_url?: string;
  slug: string;
  banner_url?: string;
  [key: string]: any;
}

interface EventsPagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface Props {
  city: City;
  countries: Country[];
  areas: Area[];
  events?: Event[];
  events_pagination?: EventsPagination;
  errors?: string[];
}

const theme = createTheme({
  primaryColor: 'orange',
  colors: {
    orange: [
      '#FEF5ED',
      '#FDE3D1',
      '#FCB693',
      '#FA9D73',
      '#F87F4D',
      '#F66A33',
      '#EA580C',
      '#C44B0A',
      '#9E3D08',
      '#783006',
    ],
  },
  fontFamily: 'Poppins, sans-serif',
  headings: { fontFamily: 'Poppins, sans-serif' },
});

export default function CitiesEdit({ city, countries, areas, events = [], events_pagination, errors }: Props) {
  const [loading, setLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);

  const form = useForm({
    initialValues: {
      name: city.name,
      country_id: city.country_id ? city.country_id.toString() : '',
      area_id: city.area_id ? city.area_id.toString() : '',
    },
    validate: {
      name: (value) => (!value?.trim() ? 'City name is required' : null),
      country_id: (value) => (!value ? 'Country is required' : null),
      area_id: (value) => (!value ? 'Area is required' : null),
    },
  });

  // Filter areas based on selected country
  const filteredAreas = form.values.country_id
    ? areas.filter((area) => area.country_id.toString() === form.values.country_id)
    : [];

  // Clear area when country changes
  useEffect(() => {
    if (form.values.country_id && form.values.area_id) {
      const selectedArea = areas.find((area) => area.id.toString() === form.values.area_id);
      if (selectedArea && selectedArea.country_id.toString() !== form.values.country_id) {
        form.setFieldValue('area_id', '');
      }
    }
  }, [form.values.country_id]);

  const handleSubmit = (values: typeof form.values) => {
    setLoading(true);

    router.patch(
      `/admin/city-index/${city.id}`,
      {
        city: {
          name: values.name.trim(),
          area_id: parseInt(values.area_id),
        },
      },
      {
        onFinish: () => setLoading(false),
        onSuccess: () => {
          showNotification({
            color: 'green',
            title: 'Update Successful',
            message: `City "${values.name}" was successfully updated!`,
            icon: <IconCheck />,
            autoClose: 5000,
            position: 'top-right',
          });
        },
        onError: (errors) => {
          console.error('Update failed:', errors);
          showNotification({
            color: 'red',
            title: 'Update Failed',
            message: 'There was an error updating the city. Please try again.',
            icon: <IconAlertCircle />,
            autoClose: 5000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const handleEventsPageChange = (page: number) => {
    setEventsLoading(true);

    const params = new URLSearchParams(window.location.search);
    params.set('page', page.toString());
    if (events_pagination?.per_page) {
      params.set('per_page', events_pagination.per_page.toString());
    }

    router.get(
      `/admin/city-index/${city.id}/edit?${params.toString()}`,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        onFinish: () => setEventsLoading(false),
      },
    );
  };

  const handleBack = () => {
    router.get('/admin/city-index');
  };

  const breadcrumbItems = [
    { title: 'Cities', href: '/admin/city-index' },
    { title: `Edit ${city.name}`, href: null },
  ].map((item, index) =>
    item.href ? (
      <Anchor key={index} onClick={() => router.get(item.href!)}>
        {item.title}
      </Anchor>
    ) : (
      <span key={index} className="text-white">
        {item.title}
      </span>
    ),
  );

  return (
    <MantineProvider theme={theme}>
      <Notifications />
      <Container size="xl" py="md">
        <Stack gap="lg">
          {/* Breadcrumbs */}
          <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>

          {/* Header */}
          <Group justify="space-between">
            <Title order={1} className="rubik-header">
              Edit City
            </Title>
            <Button variant="light" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
              Back to Cities
            </Button>
          </Group>

          {/* Error Messages */}
          {errors && errors.length > 0 && (
            <Alert icon={<IconAlertCircle size={16} />} title="Please fix the following errors:" color="red">
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Show warning if city has missing data */}
          {(!city.area_id || !city.country_id) && (
            <Alert icon={<IconAlertCircle size={16} />} title="Missing Data Warning" color="yellow">
              This city is missing some required data. Please ensure both country and area are selected.
            </Alert>
          )}

          {/* Form */}
          <Paper shadow="sm" p="xl" radius="md" style={{ backgroundColor: '#75757520' }}>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="lg">
                <TextInput label="City Name" placeholder="Enter city name" required {...form.getInputProps('name')} />

                <Select
                  label="Country"
                  placeholder="Select a country"
                  className="text-white"
                  data={countries.map((country) => ({
                    value: country.id.toString(),
                    label: `${country.name} (${country.continent_name || 'Unknown Continent'})`,
                  }))}
                  required
                  searchable
                  {...form.getInputProps('country_id')}
                />

                <Select
                  label="Area"
                  placeholder={form.values.country_id ? 'Select an area' : 'Select a country first'}
                  data={filteredAreas.map((area) => ({
                    value: area.id.toString(),
                    label: area.name,
                  }))}
                  required
                  searchable
                  disabled={!form.values.country_id}
                  {...form.getInputProps('area_id')}
                />

                <Group justify="flex-end" mt="md">
                  <Button variant="light" onClick={handleBack} disabled={loading}>
                    Cancel
                  </Button>
                  <Button type="submit" color="#EA580C" loading={loading} leftSection={<IconDeviceFloppy size={16} />}>
                    Save Changes
                  </Button>
                </Group>
              </Stack>
            </form>
          </Paper>

          {/* Additional Info */}
          <Paper p="md" radius="md" bg="#17171750">
            <Stack gap="xs">
              <Title order={4} c="white" className="poppins">
                Current Information
              </Title>
              <Group>
                <Box c="white">
                  <strong>City ID:</strong> {city.id}
                </Box>
                <Box c="white">
                  <strong>Area ID:</strong> {city.area_id || 'Not set'}
                </Box>
                <Box c="white">
                  <strong>Country ID:</strong> {city.country_id || 'Not set'}
                </Box>
              </Group>
            </Stack>
          </Paper>

          {/* Events Section */}
          {events_pagination && events_pagination.total_count > 0 && (
            <Paper p="md" radius="md" bg="#17171750">
              <Stack gap="md">
                {/* Events Title with Loading */}
                <Group justify="space-between" align="center">
                  <Group align="center">
                    <Title order={4} c="white" className="poppins">
                      Events in {city.name} ({events_pagination.total_count})
                    </Title>
                    {eventsLoading && <Loader size="sm" color="#EA580C" />}
                  </Group>
                  <Text size="sm" c="gray.4">
                    Showing {(events_pagination.current_page - 1) * events_pagination.per_page + 1}-
                    {Math.min(
                      events_pagination.current_page * events_pagination.per_page,
                      events_pagination.total_count,
                    )}{' '}
                    of {events_pagination.total_count}
                  </Text>
                </Group>

                {/* Top Pagination */}
                {events_pagination.total_pages > 1 && (
                  <Flex justify="flex-start">
                    <StablePagination
                      total={events_pagination.total_pages}
                      value={events_pagination.current_page}
                      onChange={handleEventsPageChange}
                    />
                  </Flex>
                )}

                {/* Events Grid with Loading Overlay */}
                <Box style={{ position: 'relative' }}>
                  {eventsLoading && (
                    <Box
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 10,
                        borderRadius: '8px',
                      }}
                    />
                  )}

                  <SimpleGrid
                    cols={{ base: 1, sm: 2, md: 3, lg: 4 }}
                    spacing="md"
                    verticalSpacing="md"
                    style={{
                      opacity: eventsLoading ? 0.6 : 1,
                      transition: 'opacity 0.2s ease-in-out',
                    }}
                  >
                    {events.map((event) => (
                      <EventCard
                        key={event.id}
                        event={event}
                        onCardClick={(event) => {
                          router.get(`/events/${event.slug}`);
                        }}
                      />
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Bottom Pagination */}
                {events_pagination.total_pages > 1 && (
                  <Flex justify="flex-start">
                    <StablePagination
                      total={events_pagination.total_pages}
                      value={events_pagination.current_page}
                      onChange={handleEventsPageChange}
                    />
                  </Flex>
                )}
              </Stack>
            </Paper>
          )}

          {/* No Events Message */}
          {events_pagination && events_pagination.total_count === 0 && (
            <Paper p="md" radius="md" bg="#17171750">
              <Stack gap="md" align="center">
                <Title order={4} c="white" className="poppins">
                  No Events Found
                </Title>
                <Text c="gray.4" ta="center" size="lg">
                  There are currently no events scheduled directly in {city.name}.
                </Text>
                <Text c="gray.6" ta="center" size="sm">
                  Events may exist in the broader area, but none are specifically tagged to this city.
                </Text>
              </Stack>
            </Paper>
          )}

          {/* Loading State for Events when no pagination exists yet */}
          {!events_pagination && eventsLoading && (
            <Paper p="md" radius="md" bg="#17171750">
              <Stack gap="md" align="center">
                <Group align="center">
                  <Title order={4} c="white" className="poppins">
                    Loading Events for {city.name}
                  </Title>
                  <Loader size="sm" color="#EA580C" />
                </Group>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>
    </MantineProvider>
  );
}
